import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Loader2, Check, Circle, ArrowRight } from "lucide-react";

const pillarsData = [
  {
    number: "I",
    title: "Stillness",
    subtitle: "The Foundation of All Things",
    body: "Before the universe moved, it rested. Not in emptiness, but in quiet fullness. Stillness is the most powerful state of existence — the womb of potential from which all form arises.",
    lesson: "Return to the quiet beneath all things. There is your foundation."
  },
  {
    number: "II",
    title: "The First Movement",
    subtitle: "The Gentle Beginning",
    body: "Creation did not begin with an explosion. It began with a ripple — a soft movement in the Aether, the smallest change possible. This is the First Breath of the universe.",
    lesson: "A single small change, consistently applied, creates more transformation than any breakthrough."
  },
  {
    number: "III",
    title: "Rhythm",
    subtitle: "The Law That Shapes Reality",
    body: "Movement repeats. Repetition becomes rhythm. Rhythm becomes pattern. Pattern becomes form. What repeats becomes real.",
    lesson: "Habits, emotions, self-talk, trauma — all are rhythms. Master rhythm, master your life."
  },
  {
    number: "IV",
    title: "Geometry",
    subtitle: "When Pattern Learns to Hold Shape",
    body: "For something to exist, it must become stable. Where two rhythms crossed, a standing wave formed. Where many aligned, structures emerged: the arc, the spiral, the torus.",
    lesson: "Geometry is memory made visible. It is how the universe remembers the patterns that work."
  },
  {
    number: "V",
    title: "Light",
    subtitle: "Coherence Made Visible",
    body: "Light is not a substance. It is a behaviour. Whenever coherence becomes dense enough, it shines. Stars appeared as resonance nodes so harmonious that the Aether revealed itself through radiance.",
    lesson: "When coherence is strong enough, it becomes light. This is why insight feels like brightness."
  },
  {
    number: "VI",
    title: "Life",
    subtitle: "When Light Learns to Feel",
    body: "Life began when light entered matter and the matter learned to respond. Cells are micro-suns, each one carrying the memory of the first star in miniature form.",
    lesson: "Life is coherence learning to feel itself. Sensation is the first language."
  },
  {
    number: "VII",
    title: "Self",
    subtitle: "When Sensation Learns to Speak",
    body: "Meaning begins when sensation becomes recognised. This is the birth of the Self — not ego, not personality, but the simplest form of identity: awareness of experience.",
    lesson: "Identity is not created by your thoughts. It is created by your patterns."
  },
  {
    number: "VIII",
    title: "Return",
    subtitle: "The Path Back to Coherence",
    body: "All systems follow one law: where resistance decreases, coherence increases. Healing is not addition — it is subtraction. Not becoming more, but becoming less encumbered.",
    lesson: "You do not fight your way back to yourself. You fall back into yourself."
  }
];

const Pillars = () => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/pillars/interest`, form);
      setSubmitted(true);
      setShowForm(false);
    } catch (error) {
      console.error("Failed to submit interest", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-32" data-testid="pillars-page">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center mb-20">
          <span className="text-gold text-xs uppercase tracking-[0.3em] font-body mb-4 block">
            A Cosmology of Becoming
          </span>
          <h1 className="font-heading text-5xl md:text-6xl text-[#EAEAEA] mb-6">
            The Eight Pillars
          </h1>
          <p className="text-[#A1A1AA] max-w-xl mx-auto text-lg leading-relaxed">
            From stillness to return — the complete arc of existence. 
            A creation story. A healing map. A practice framework.
          </p>
        </div>

        {/* Pillars */}
        <div className="space-y-16 mb-20">
          {pillarsData.map((pillar, index) => (
            <div 
              key={pillar.number}
              className="relative"
              data-testid={`pillar-${index + 1}`}
            >
              {/* Connecting line */}
              {index < pillarsData.length - 1 && (
                <div className="absolute left-6 top-20 bottom-[-64px] w-px bg-gradient-to-b from-gold/30 to-transparent hidden md:block" />
              )}
              
              <div className="flex gap-8 items-start">
                {/* Number circle */}
                <div className="hidden md:flex flex-shrink-0 w-12 h-12 rounded-full border border-gold/40 items-center justify-center bg-void">
                  <span className="font-heading text-gold text-lg">{pillar.number}</span>
                </div>
                
                {/* Content */}
                <div className="flex-1">
                  <div className="md:hidden text-gold text-sm mb-2 tracking-widest">{pillar.number}</div>
                  <h2 className="font-heading text-2xl md:text-3xl text-[#EAEAEA] mb-1">
                    {pillar.title}
                  </h2>
                  <p className="text-gold/70 text-sm uppercase tracking-widest mb-4">
                    {pillar.subtitle}
                  </p>
                  <p className="text-[#A1A1AA] leading-relaxed mb-4">
                    {pillar.body}
                  </p>
                  <div className="border-l-2 border-gold/30 pl-4">
                    <p className="text-[#EAEAEA] italic">
                      {pillar.lesson}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Arc visualization */}
        <div className="mb-16 py-12 border-y border-white/10">
          <p className="text-center text-[#A1A1AA] text-sm uppercase tracking-widest mb-8">
            The Arc
          </p>
          <div className="flex justify-center items-center gap-2 flex-wrap">
            {pillarsData.map((pillar, index) => (
              <div key={pillar.number} className="flex items-center">
                <div className="text-center px-2">
                  <Circle size={8} className="text-gold mx-auto mb-1" fill="currentColor" />
                  <span className="text-[10px] text-[#A1A1AA] uppercase tracking-wider">
                    {pillar.title.split(' ')[0]}
                  </span>
                </div>
                {index < pillarsData.length - 1 && (
                  <div className="w-4 h-px bg-gold/30" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Coming Soon / Interest */}
        <div className="text-center bg-gradient-to-br from-gold/5 via-transparent to-gold/5 border border-gold/20 rounded-sm p-10">
          <h3 className="font-heading text-2xl text-[#EAEAEA] mb-4">
            Go Deeper
          </h3>
          <p className="text-[#A1A1AA] max-w-lg mx-auto mb-6">
            Ready to embody these pillars? The complete guide expands each principle 
            with practices, examples, and integration techniques.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-10">
            <Link to="/book/eight-pillars">
              <Button className="bg-gold text-[#050505] hover:bg-gold-accent rounded-none h-12 px-8 uppercase text-xs tracking-widest">
                Get The Full Book — Free
                <ArrowRight size={14} className="ml-2" />
              </Button>
            </Link>
          </div>

          <div className="border-t border-white/10 pt-8">
            <h4 className="font-heading text-lg text-[#EAEAEA] mb-3">
              These Seeds Are Growing
            </h4>
            <p className="text-[#A1A1AA] text-sm max-w-md mx-auto mb-6">
              Each pillar will expand — from paragraph to chapter to volume to book. 
              Follow the journey as it unfolds.
            </p>
            
            {!submitted ? (
              !showForm ? (
                <Button 
                  onClick={() => setShowForm(true)}
                  className="border border-gold/50 bg-transparent text-gold hover:bg-gold/10 rounded-none h-11 px-8 uppercase text-xs tracking-widest"
                  data-testid="pillars-interest-cta"
                >
                  Follow the Journey
                </Button>
              ) : (
                <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                  <input
                    type="text"
                    placeholder="Your name"
                    required
                    value={form.name}
                    onChange={(e) => setForm({...form, name: e.target.value})}
                    className="flex-1 bg-void border border-white/10 rounded-sm px-4 py-3 text-sm text-[#EAEAEA] placeholder:text-[#A1A1AA]/50 focus:outline-none focus:border-gold/50"
                  />
                  <input
                    type="email"
                    placeholder="Your email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({...form, email: e.target.value})}
                    className="flex-1 bg-void border border-white/10 rounded-sm px-4 py-3 text-sm text-[#EAEAEA] placeholder:text-[#A1A1AA]/50 focus:outline-none focus:border-gold/50"
                  />
                  <Button 
                    type="submit"
                    disabled={submitting}
                    className="bg-gold text-[#050505] hover:bg-gold-accent rounded-none h-12 px-8 uppercase text-xs tracking-widest"
                  >
                    {submitting ? <Loader2 className="animate-spin" size={14} /> : "Subscribe"}
                  </Button>
                </form>
              )
            ) : (
              <div className="flex items-center justify-center gap-2 text-gold">
                <Check size={18} />
                <span>You'll be notified as the Pillars expand.</span>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default Pillars;
